<template>
  <ConfigErrorBoundary>
    <FileConverter
      :config="converterConfig"
      @batch-created="handleBatchCreated"
      @job-completed="handleJobCompleted"
      @batch-cleared="handleBatchCleared"
      @error="handleError"
      @credits-updated="handleCreditsUpdated"
      @files-selected="handleFilesSelected"
    />
  </ConfigErrorBoundary>
</template>

<script setup lang="ts">
import FileConverter from './FileConverter.vue';
import ConfigErrorBoundary from './ConfigErrorBoundary.vue';
import { parseAndValidateConverterConfig } from '../utils/ConfigValidator';

const H2_ID = 'png-to-ico-h2';
const defaultH2 =
  'Generates 16/32/48/64/128/256px layers with perfect transparency for Windows and favicons.';
const warningH2 =
  '⚠️ For best results, upload images at least 256×256 pixels. Smaller files will be upscaled automatically.';

const converterConfig = parseAndValidateConverterConfig('png-to-ico-converter', 'PNG → ICO');

function getH2(): HTMLElement | null {
  return document.getElementById(H2_ID);
}

function setWarning(active: boolean) {
  const el = getH2();
  if (!el) return;
  el.textContent = active ? warningH2 : defaultH2;
  el.classList.toggle('text-amber-700', active);
}

async function handleFilesSelected(files: File[]) {
  if (files.length === 0) {
    setWarning(false);
    return;
  }

  const first = files[0];
  const dims = await readImageSize(first).catch(() => null);
  if (!dims) {
    setWarning(false);
    return;
  }

  const needsWarning = dims.width < 256 || dims.height < 256;
  setWarning(needsWarning);
}

function handleBatchCreated(batchId: string) {
  console.log('[PNG→ICO] Batch created:', batchId);
}

function handleJobCompleted(jobId: string) {
  console.log('[PNG→ICO] Job completed:', jobId);
}

function handleBatchCleared() {
  setWarning(false);
  console.log('[PNG→ICO] Batch cleared');
}

function handleError(error: Error) {
  console.error('[PNG→ICO] Error:', error);
}

function handleCreditsUpdated(credits: number) {
  console.log('[PNG→ICO] Credits updated:', credits);
}

function readImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to read image dimensions'));
    img.src = URL.createObjectURL(file);
  });
}
</script>
